export const PRODUCT_DIAGNOSIS_ACTION_TYPES = [
  'connect_source',
  'provide_input',
  'edit_tool',
  'repair_tool',
  'retry',
  'open_run_details',
  // The failure is on Domino's side: the person cannot fix it and tells us.
  'contact_support',
] as const;

export type ProductDiagnosisActionType = typeof PRODUCT_DIAGNOSIS_ACTION_TYPES[number];

export interface ProductDiagnosisAction {
  type: ProductDiagnosisActionType;
  label: string;
  payload?: Record<string, unknown>;
}

export interface ProductDiagnosis {
  title: string;
  explanation: string;
  primaryAction: ProductDiagnosisAction;
  secondaryActions: ProductDiagnosisAction[];
  userCanFix: boolean;
  canAutoRepair: boolean;
  confidence: 'low' | 'medium' | 'high';
  generatedAt?: string;
  model?: string;
  provider?: string;
}

/**
 * Technical, planner-facing diagnosis of a failed step. Unlike ProductDiagnosis,
 * this is NEVER shown to the user: `repairHint` is a precise engineering
 * instruction (it may name primitives, fields and codes) used to drive the
 * composer's repair pass. Built deterministically at failure time so repair
 * works even when the user-facing LLM diagnosis could not be generated.
 */
export interface InternalDiagnosis {
  stepPosition: number;
  actionType: string;
  errorCode?: string;
  runtimeIssueCode?: string;
  /** Internal repair instruction sent to the composer planner. Not user copy. */
  repairHint: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isActionType = (value: unknown): value is ProductDiagnosisActionType =>
  typeof value === 'string' &&
  PRODUCT_DIAGNOSIS_ACTION_TYPES.includes(value as ProductDiagnosisActionType);

const normalizeText = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

export const parseProductDiagnosisAction = (
  value: unknown,
): ProductDiagnosisAction | null => {
  if (!isRecord(value) || !isActionType(value.type)) return null;
  const label = normalizeText(value.label);
  if (!label) return null;
  return {
    type: value.type,
    label,
    ...(isRecord(value.payload) ? { payload: value.payload } : {}),
  };
};

export const parseProductDiagnosis = (
  value: unknown,
): ProductDiagnosis | null => {
  if (!isRecord(value)) return null;

  const title = normalizeText(value.title);
  const explanation = normalizeText(value.explanation);
  const primaryAction = parseProductDiagnosisAction(value.primaryAction);
  const secondaryActionsRaw = Array.isArray(value.secondaryActions)
    ? value.secondaryActions
    : [];
  const secondaryActions = secondaryActionsRaw
    .map(parseProductDiagnosisAction)
    .filter((action): action is ProductDiagnosisAction => action !== null);
  const confidence = value.confidence;

  if (
    !title ||
    !explanation ||
    !primaryAction ||
    (confidence !== 'low' && confidence !== 'medium' && confidence !== 'high') ||
    typeof value.userCanFix !== 'boolean' ||
    typeof value.canAutoRepair !== 'boolean'
  ) {
    return null;
  }

  const generatedAt = normalizeText(value.generatedAt);
  const model = normalizeText(value.model);
  const provider = normalizeText(value.provider);

  return {
    title,
    explanation,
    primaryAction,
    secondaryActions,
    userCanFix: value.userCanFix,
    canAutoRepair: value.canAutoRepair,
    confidence,
    ...(generatedAt ? { generatedAt } : {}),
    ...(model ? { model } : {}),
    ...(provider ? { provider } : {}),
  };
};

export const extractProductDiagnosisFromError = (
  value: unknown,
): ProductDiagnosis | null => {
  if (!isRecord(value)) return null;
  return parseProductDiagnosis(value.productDiagnosis);
};

export const parseInternalDiagnosis = (
  value: unknown,
): InternalDiagnosis | null => {
  if (!isRecord(value)) return null;
  const actionType = normalizeText(value.actionType);
  const repairHint = normalizeText(value.repairHint);
  const stepPosition = typeof value.stepPosition === 'number' &&
      Number.isFinite(value.stepPosition)
    ? value.stepPosition
    : null;
  if (!actionType || !repairHint || stepPosition === null) return null;

  const errorCode = normalizeText(value.errorCode);
  const runtimeIssueCode = normalizeText(value.runtimeIssueCode);
  return {
    stepPosition,
    actionType,
    repairHint,
    ...(errorCode ? { errorCode } : {}),
    ...(runtimeIssueCode ? { runtimeIssueCode } : {}),
  };
};

export const extractInternalDiagnosisFromError = (
  value: unknown,
): InternalDiagnosis | null => {
  if (!isRecord(value)) return null;
  return parseInternalDiagnosis(value.internalDiagnosis);
};
